import React, { useState } from 'react'
import { ClipboardText, Envelope, Key, Link, PencilLine, Code } from '@phosphor-icons/react'
import { copyToClipboard, toast } from '../utils'
import ModalEditProjectPublicView from './ModalEditProjectPublicView'

const ProjectCredentials = ({ project, update_credentials_project_path, projectPublicDomainPath, update_public_view_project_path, mailgun_domain, authToken, apiEndpoint }) => {
  const [webhookUrl, setWebhookUrl] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})
  const [open, setOpen] = useState(false)
  const [emailAddress, setEmailAddress] = useState(project?.email_address)
  const [apiKey, setApiKey] = useState(project?.api_key)
  const [publicProjectToken, setPublicProjectToken] = useState(project?.public_project_token)
  const [webhookCredentials, setWebhookCredentials] = useState({
    url: project.webhook_url,
    token: project.webhook_token,
    secret: project.webhook_secret
  })

  const randomTitles = [
    "The Future of AI in Everyday Life",
    "Exploring the Depths of Machine Learning",
    "Digital Transformation in Modern Business",
    "The Rise of Quantum Computing",
    "Blockchain Technology: Beyond Cryptocurrency",
    "The Impact of Big Data on Society",
    "Cybersecurity in the Age of IoT",
    "The Evolution of Cloud Computing",
    "Augmented Reality: Changing How We See the World",
    "The Ethics of Artificial Intelligence"
  ]

  const generateRandomTitle = () => {
    return randomTitles[Math.floor(Math.random() * randomTitles.length)]
  }

  const generateRandomBody = () => {
    const loremIpsum = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
    return loremIpsum.substring(0, 50 + Math.floor(Math.random() * 50))
  }

  const exampleTitle = generateRandomTitle()
  const exampleBody = generateRandomBody()

  const handleCredentialUpdate = async (type, enable = true) => {
    setIsSubmitting(true)

    let formData = {}
    switch(type) {
      case 'email':
        formData = { email_address: enable ? 'true' : null }
        break
      case 'api':
        formData = { api_key: enable ? 'true' : null }
        break
      case 'webhook':
        formData = { webhook_url: enable ? webhookUrl : null }
        break
      case 'publicProjectToken':
        formData = { public_project_token: enable ? 'true' : null }
        break
    }

    try {
      const response = await fetch(update_credentials_project_path, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ project: formData })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to update')
      }

      const data = await response.json()
      
      switch(type) {
        case 'email':
          setEmailAddress(enable ? data.email_address : null)
          break
        case 'api':
          setApiKey(enable ? data.api_key : null)
          break
        case 'publicProjectToken':
          setPublicProjectToken(enable ? data.public_project_token : null)
          break
        case 'webhook':
          setWebhookCredentials({
            url: enable ? webhookUrl : null,
            token: enable ? data.webhook_token : null,
            secret: enable ? data.webhook_secret : null
          })
          if (enable) setWebhookUrl('')
          break
      }

    } catch (error) {
      setErrors({ ...errors, submit: error.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const validateWebhookUrl = (url) => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const validateForm = (data) => {
    const newErrors = {}

    if (data.webhook_url) {
      if (!validateWebhookUrl(data.webhook_url)) {
        newErrors.webhook_url = 'Please enter a valid URL'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (formData) => {
    if (formData.webhook_url && !validateForm({ webhook_url: formData.webhook_url })) return
    
    if (formData.hasOwnProperty('email_address')) {
      await handleCredentialUpdate('email', formData.email_address !== null)
    } else if (formData.hasOwnProperty('api_key')) {
      await handleCredentialUpdate('api', formData.api_key !== null)
    } else if (formData.hasOwnProperty('webhook_url')) {
      await handleCredentialUpdate('webhook', formData.webhook_url !== null)
    } else if (formData.hasOwnProperty('public_project_token')) {
      await handleCredentialUpdate('publicProjectToken', formData.public_project_token !== null)
    }
  }

  const handleCopyToClipboard = async (text) => {
    copyToClipboard(text)
    toast({message: 'Copied to clipboard!', type: 'success', duration: 3000, gravity: 'top', position: 'right', close: true})
  }

  const updateProjectPublicView = async (project) => {
    setOpen(false)
    try {
      const response = await fetch(update_public_view_project_path, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({project: project})
      })

      if (!response.ok) {
        const errorData = await response.json()
        toast({message: errorData.message || 'Failed to update', type: 'error', duration: 3000, gravity: 'top', position: 'right', close: true})
      }

      const data = await response.json()
      toast({message: 'Project public view updated', type: 'success', duration: 3000, gravity: 'top', position: 'right', close: true})
    } catch (error) {
      setErrors({ ...errors, submit: error.message })
      toast({message: error.message || 'Failed to update', type: 'error', duration: 3000, gravity: 'top', position: 'right', close: true})
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-8">
      <ModalEditProjectPublicView
        open={open}
        onClose={() => setOpen(false)}
        onUpdate={updateProjectPublicView}
        project={project}
      />

      {/* Email Section */}
      <div className="card bg-base-200 shadow-sm">
        <div className="card-body">
          <h3 className="card-title text-lg font-medium flex items-center gap-2">
            <Envelope className="h-5 w-5 text-primary"/>
            Email Integration
          </h3>

          <div className="form-control w-full">
            {emailAddress ? (
              <>
                <div className="flex items-center justify-between bg-base-100 p-4 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Project Email Address:</span>
                    <p className="text-lg font-mono">{`project-${emailAddress}@${mailgun_domain}`}</p>
                  </div>
                  <button
                    className="btn btn-ghost btn-xs tooltip inline-flex"
                    onClick={() => handleCopyToClipboard(`project-${emailAddress}@${mailgun_domain}`)}
                    title="Copy to clipboard"
                    disabled={isSubmitting}
                  >
                    <ClipboardText className="h-3 w-3"/>
                  </button>
                </div>
                <p className="text-sm text-base-content/70 mt-2">Send emails to this address to automatically create content</p>
                <div className="mt-4">
                  <button
                    className="btn btn-outline btn-error btn-xs"
                    onClick={() => handleSubmit({ email_address: null })}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Disabling...' : 'Disable Email Integration'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-2">
                  <button
                    className="btn btn-primary btn-xs"
                    onClick={() => handleSubmit({ email_address: true })}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Enabling...' : 'Enable Email Integration'}
                  </button>
                </div>
                <p className="text-sm text-base-content/70 mt-2">Enable email integration to receive content via email</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* API Section */}
      <div className="card bg-base-200 shadow-sm">
        <div className="card-body">
          <h3 className="card-title text-lg font-medium flex items-center gap-2">
            <Key className="h-5 w-5 text-primary"/>
            API Access
          </h3>

          <div className="form-control w-full">
            {apiKey ? (
              <>
                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-base-100 p-4 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Project Token:</span>
                      <p className="text-lg font-mono">{apiKey}</p>
                    </div>
                    <button
                      className="btn btn-ghost btn-xs tooltip inline-flex"
                      onClick={() => handleCopyToClipboard(apiKey)}
                      title="Copy project token to clipboard"
                      disabled={isSubmitting}
                    >
                      <ClipboardText className="h-3 w-3"/>
                    </button>
                  </div>
                  <div className="flex items-center justify-between bg-base-100 p-4 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Auth Token:</span>
                      <p className="text-lg font-mono">{authToken}</p>
                    </div>
                    <button
                      className="btn btn-ghost btn-xs tooltip inline-flex"
                      onClick={() => handleCopyToClipboard(authToken)}
                      title="Copy auth token to clipboard"
                      disabled={isSubmitting}
                    >
                      <ClipboardText className="h-3 w-3"/>
                    </button>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="bg-base-100 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Code className="h-5 w-5 text-primary"/>
                      <h4 className="font-medium">Example API Request:</h4>
                    </div>
                    <pre className="bg-base-200 p-4 rounded text-sm overflow-x-auto font-mono">
{`curl -X POST \\
  -H "Authorization: Bearer ${authToken}" \\
  -H "Content-Type: application/json" \\
  -d '{"content": {"title": "${exampleTitle}", "body": "${exampleBody}", "options": {"record_id": 1}}}' \\
  "${apiEndpoint}/?api_key=${apiKey}"`}
                    </pre>
                    <div className="flex justify-end mt-2">
                      <button
                        className="btn btn-ghost btn-xs tooltip inline-flex"
                        onClick={() => handleCopyToClipboard(`curl -X POST \\
  -H "Authorization: Bearer ${authToken}" \\
  -H "Content-Type: application/json" \\
  -d '{"content": {"title": "${exampleTitle}", "body": "${exampleBody}", "options": {"record_id": 1}}}' \\
  "${apiEndpoint}/?api_key=${apiKey}"`)}
                        title="Copy curl command to clipboard"
                        disabled={isSubmitting}
                      >
                        <ClipboardText className="h-3 w-3"/>
                        Copy curl command
                      </button>
                    </div>
                    <p className="text-sm mt-2 text-base-content/70">
                      Note: The API key is used to identify the project, while the auth token authenticates the user.
                      Title must be between 3 and 500 characters, body must be between 50 and 25,000 characters. 
                      You can use options to pass any additional information you want to store with the content.
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    className="btn btn-outline btn-error btn-xs"
                    onClick={() => handleSubmit({ api_key: null })}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Disabling...' : 'Disable API Access'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-2">
                  <button
                    className="btn btn-primary btn-xs"
                    onClick={() => handleSubmit({ api_key: true })}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Generating...' : 'Generate API Key'}
                  </button>
                </div>
                <p className="text-sm text-base-content/70 mt-2">Enable API access to integrate via REST API</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Webhook Section */}
      <div className="card bg-base-200 shadow-sm">
        <div className="card-body">
          <h3 className="card-title text-lg font-medium flex items-center gap-2">
            <Link className="h-5 w-5 text-primary"/>
            Webhook Integration
          </h3>

          <div className="form-control w-full">
            {webhookCredentials.url ? (
              <>
                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-base-100 p-4 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Webhook URL:</span>
                      <p className="text-lg font-mono">{webhookCredentials.url}</p>
                    </div>
                    <button
                      className="btn btn-ghost btn-xs tooltip inline-flex"
                      onClick={() => handleCopyToClipboard(webhookCredentials.url)}
                      title="Copy URL to clipboard"
                      disabled={isSubmitting}
                    >
                      <ClipboardText className="h-3 w-3"/>
                    </button>
                  </div>
                  <div className="flex items-center justify-between bg-base-100 p-4 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Webhook Token:</span>
                      <p className="text-lg font-mono">{webhookCredentials.token}</p>
                    </div>
                    <button
                      className="btn btn-ghost btn-xs tooltip inline-flex"
                      onClick={() => handleCopyToClipboard(webhookCredentials.token)}
                      title="Copy token to clipboard"
                      disabled={isSubmitting}
                    >
                      <ClipboardText className="h-3 w-3"/>
                    </button>
                  </div>
                  <div className="flex items-center justify-between bg-base-100 p-4 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Webhook Secret:</span>
                      <p className="text-lg font-mono">{webhookCredentials.secret}</p>
                    </div>
                    <button
                      className="btn btn-ghost btn-xs tooltip inline-flex"
                      onClick={() => handleCopyToClipboard(webhookCredentials.secret)}
                      title="Copy secret to clipboard"
                      disabled={isSubmitting}
                    >
                      <ClipboardText className="h-3 w-3"/>
                    </button>
                  </div>
                </div>
                <p className="text-sm text-base-content/70 mt-2">Use this URL, token, and secret for webhook integration</p>
                <div className="mt-4">
                  <button
                    className="btn btn-outline btn-error btn-xs"
                    onClick={() => handleSubmit({ webhook_url: null })}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Disabling...' : 'Disable Webhook Integration'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-2 items-center">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Enter Webhook URL"
                      className={`input input-bordered w-full input-sm ${errors.webhook_url ? 'input-error' : ''}`}
                      value={webhookUrl}
                      onChange={(e) => {
                        setWebhookUrl(e.target.value)
                        if (errors.webhook_url) {
                          setErrors({ ...errors, webhook_url: null })
                        }
                      }}
                      disabled={isSubmitting}
                    />
                    {errors.webhook_url && (
                      <p className="text-sm text-error mt-1">{errors.webhook_url}</p>
                    )}
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleSubmit({ webhook_url: webhookUrl })}
                    disabled={isSubmitting || !webhookUrl}
                  >
                    {isSubmitting ? 'Enabling...' : 'Enable Webhook Integration'}
                  </button>
                </div>
                <p className="text-sm text-base-content/70 mt-2">Enter a webhook URL to enable integration</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Public Project Token Section */}
      <div className="card bg-base-200 shadow-sm">
        <div className="card-body">
          <h3 className="card-title text-lg font-medium flex items-center gap-2">
            <Link className="h-5 w-5 text-primary"/>
            Public Project  
          </h3>

          <div className="form-control w-full">
            {publicProjectToken ? (
              <>
                <div className="flex items-center justify-between bg-base-100 p-4 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Public URL:</span>
                    <p className="text-lg font-mono">{`${projectPublicDomainPath.replace(':token', publicProjectToken)}`}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setOpen(true)}
                      className="btn btn-ghost btn-xs tooltip inline-flex"
                      disabled={isSubmitting}
                    >
                      <PencilLine className="h-3 w-3"/>
                      Edit
                    </button>
                    <button
                      className="btn btn-ghost btn-xs tooltip inline-flex"
                      onClick={() => handleCopyToClipboard(`${projectPublicDomainPath.replace(':token', publicProjectToken)}`)}
                      disabled={isSubmitting}
                    >
                      <ClipboardText className="h-3 w-3"/>
                    </button>
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    className="btn btn-outline btn-error btn-xs"
                    onClick={() => handleSubmit({ public_project_token: null })}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Disabling...' : 'Disable Public Project Access'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-2">
                  <button
                    className="btn btn-primary btn-xs"
                    onClick={() => handleSubmit({ public_project_token: true })}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Generating...' : 'Generate Public Project Access'}
                  </button>
                </div>
                <p className="text-sm text-base-content/70 mt-2">Generate a public token to access your project via web page</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProjectCredentials